import "ace-builds";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";

import { CopyOutlined, HolderOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, Flex, message } from "antd";
import classNames from "classnames";
import { useCCEntityOptional } from "components/CC/context/CCEntityContext.tsx";
import { CC_ENTITY_CONFIGS } from "components/CC/types/entityConfig.ts";
import {
    adaptBlueprintForEntity,
    BlueprintData,
    findDuplicateConnection,
    remapStepReferences,
} from "components/CC/utils/adaptBlueprint.ts";
import {
    findArrayItemByKey,
    JsonAstNode,
    JsonPathSegment,
    parseJsonWithPositions,
    resolveJsonPath,
} from "components/CC/utils/parseJsonWithPositions.ts";
import {
    BlueprintFormDataIssue,
    formatBlueprintFormDataIssue,
    normalizeBlueprintFormData,
    validateBlueprintFormData,
} from "components/CC/utils/validateBlueprintFormData.ts";
import { DrawerContent } from "components/UI/DrawerContent/DrawerContent.tsx";
import { DrawerTitle } from "components/UI/DrawerTitle/DrawerTitle.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AceEditor from "react-ace";

import type { Ace } from "ace-builds";

interface BlueprintDrawerProps {
    open: boolean;
    onClose: () => void;
    value: BlueprintData;
    onApply: (data: BlueprintData) => void;
}

const EDITOR_MIN_HEIGHT = 200;
const EDITOR_DEFAULT_HEIGHT = 500;

/**
 * Parses a JSON.parse() SyntaxError message for a 0-indexed row/column so the offending
 * spot can be pointed out in the editor. Falls back to null on engines that don't include
 * position info (older Safari/Firefox) — the generic error message is shown instead.
 */
const getJsonErrorLocation = (text: string, err: unknown): { row: number; column: number } | null => {
    if (!(err instanceof Error)) return null;

    const lineColMatch = err.message.match(/line (\d+) column (\d+)/i);
    if (lineColMatch) {
        return { row: Number(lineColMatch[1]) - 1, column: Number(lineColMatch[2]) - 1 };
    }

    const positionMatch = err.message.match(/position (\d+)/i);
    if (!positionMatch) return null;

    const lines = text.slice(0, Number(positionMatch[1])).split("\n");
    return { row: lines.length - 1, column: lines[lines.length - 1].length };
};

/**
 * Resolves a form-data validation issue to its exact 0-indexed source line, by locating the
 * node/edge with matching id and then walking the issue's Zod path (e.g. `data[0].fields[0].source`)
 * down from there. Falls back to the node/edge's own line if the specific field can't be found
 * (e.g. a required key is entirely absent from the pasted JSON, not just empty).
 */
const findIssueLine = (ast: JsonAstNode, issue: BlueprintFormDataIssue): number | null => {
    const arrayNode = resolveJsonPath(ast, [issue.entity === "edge" ? "edges" : "nodes"]);
    const itemNode = findArrayItemByKey(arrayNode, "id", issue.id);
    if (!itemNode) return null;

    const prefix: JsonPathSegment[] = issue.entity === "edge" ? ["data"] : ["data", "formData"];
    const fieldNode = resolveJsonPath(itemNode, [...prefix, ...issue.pathSegments]);
    if (fieldNode) return fieldNode.line;

    return itemNode.kind === "object" ? itemNode.line : null;
};

/**
 * Clean blueprint data for export - removes internal properties like beId
 */
const cleanForExport = (data: BlueprintData): BlueprintData => {
    const remapped = remapStepReferences(data);
    return {
        nodes: remapped.nodes.map((node) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { beId: _beId, ...cleanData } = (node.data || {}) as Record<string, unknown>;
            return {
                ...node,
                data: cleanData,
            };
        }),
        edges: remapped.edges.map((edge) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { beId: _beId, ...cleanEdgeData } = (edge.data || {}) as Record<string, unknown>;
            return {
                id: edge.id,
                source: edge.source,
                target: edge.target,
                type: edge.type,
                ...(Object.keys(cleanEdgeData).length > 0 && { data: cleanEdgeData }),
            };
        }),
    };
};

export const BlueprintDrawer = ({ open, onClose, value, onApply }: BlueprintDrawerProps) => {
    const { t } = usePaldockTranslation("components.connection-creator");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tGlobal } = usePaldockTranslation();
    const ccEntity = useCCEntityOptional();
    const [content, setContent] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [formDataIssues, setFormDataIssues] = useState<BlueprintFormDataIssue[]>([]);
    const [showAllIssues, setShowAllIssues] = useState(false);
    const [annotations, setAnnotations] = useState<Ace.Annotation[]>([]);
    const [editorHeight, setEditorHeight] = useState(EDITOR_DEFAULT_HEIGHT);
    const editorRef = useRef<Ace.Editor | null>(null);
    const resizeStateRef = useRef<{ startY: number; startHeight: number } | null>(null);

    // Applying annotations must happen after react-ace has synced `content` into the actual Ace
    // document — setting them directly inside validateContent races with react-ace's own
    // controlled-value sync (it calls editor.setValue() on the next commit when content was
    // pushed in programmatically, e.g. on drawer open), which wipes out anything set before it.
    useEffect(() => {
        editorRef.current?.getSession().setAnnotations(annotations);
    }, [annotations, content]);

    // Sync internal state with external value when drawer opens — validated immediately, since
    // the current diagram itself (e.g. a modify-field step left half-filled-in) can already be
    // invalid; the user shouldn't have to touch the editor first to find that out.
    useEffect(() => {
        if (open) {
            const cleanData = cleanForExport(value);
            const json = JSON.stringify(cleanData, null, 2);
            setContent(json);
            setShowAllIssues(false);
            validateContent(json);
        }
    }, [open, value]);

    // Custom drag handle instead of native CSS resize: the browser's own resize grip sits in the
    // same bottom-right corner as Ace's scrollbar and gets visually swallowed by it.
    const handleResizeMove = useCallback((e: MouseEvent) => {
        const resizeState = resizeStateRef.current;
        if (!resizeState) return;
        const delta = e.clientY - resizeState.startY;
        setEditorHeight(Math.max(EDITOR_MIN_HEIGHT, resizeState.startHeight + delta));
    }, []);

    const handleResizeEnd = useCallback(() => {
        resizeStateRef.current = null;
        document.removeEventListener("mousemove", handleResizeMove);
        document.removeEventListener("mouseup", handleResizeEnd);
    }, [handleResizeMove]);

    const handleResizeStart = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            resizeStateRef.current = { startY: e.clientY, startHeight: editorHeight };
            document.addEventListener("mousemove", handleResizeMove);
            document.addEventListener("mouseup", handleResizeEnd);
        },
        [editorHeight, handleResizeMove, handleResizeEnd]
    );

    // Detach any straggling listeners if the drawer unmounts mid-drag
    useEffect(() => () => handleResizeEnd(), [handleResizeEnd]);

    // Builds a summary message ("first issue (+N more)") for the alert's headline; the full
    // list is rendered separately behind "show all issues" so it doesn't get lost mid-sentence.
    const buildFormDataError = useCallback(
        (issues: BlueprintFormDataIssue[]): string => {
            const [first, ...rest] = issues;
            const detail = formatBlueprintFormDataIssue(tGlobal, first);
            const key =
                first.entity === "edge"
                    ? "blueprint.error_invalid_connection_data"
                    : "blueprint.error_invalid_step_data";
            const message = t(key, { details: detail });
            return rest.length > 0
                ? `${message} ${t("blueprint.error_invalid_data_more", { count: rest.length })}`
                : message;
        },
        [t, tGlobal]
    );

    // Validates JSON structure + node/edge data shape. Shared by the initial-open sync, live
    // typing, and (indirectly, via the same schemas) Apply — so an already-invalid diagram is
    // flagged immediately instead of only after the user edits something.
    const validateContent = useCallback(
        (newContent: string) => {
            setFormDataIssues([]);
            setAnnotations([]);

            try {
                const parsed = JSON.parse(newContent) as BlueprintData;
                if (!parsed.nodes || !Array.isArray(parsed.nodes)) {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_nodes"));
                    return;
                }
                if (!parsed.edges || !Array.isArray(parsed.edges)) {
                    setIsValid(false);
                    setError(t("blueprint.error_invalid_edges"));
                    return;
                }
                const duplicate = findDuplicateConnection(parsed.edges);
                if (duplicate) {
                    setIsValid(false);
                    setError(
                        t("blueprint.error_duplicate_connection", {
                            source: duplicate.source,
                            target: duplicate.target,
                        })
                    );
                    return;
                }
                const issues = validateBlueprintFormData(parsed);
                if (issues.length > 0) {
                    setIsValid(false);
                    setFormDataIssues(issues);
                    setError(buildFormDataError(issues));

                    const ast = parseJsonWithPositions(newContent);
                    const messagesByRow = new Map<number, string[]>();
                    issues.forEach((issue) => {
                        const row = findIssueLine(ast, issue);
                        if (row === null) return;
                        messagesByRow.set(row, [
                            ...(messagesByRow.get(row) ?? []),
                            formatBlueprintFormDataIssue(tGlobal, issue),
                        ]);
                    });
                    setAnnotations(
                        Array.from(messagesByRow.entries()).map(([row, messages]) => ({
                            row,
                            column: 0,
                            text: messages.join("\n"),
                            type: "error",
                        }))
                    );
                    return;
                }
                setIsValid(true);
                setError(null);
            } catch (err) {
                setIsValid(false);
                const location = getJsonErrorLocation(newContent, err);
                if (location) {
                    setError(
                        t("blueprint.error_invalid_json_at", { line: location.row + 1, column: location.column + 1 })
                    );
                    setAnnotations([{ ...location, text: (err as Error).message, type: "error" }]);
                } else {
                    setError(t("blueprint.error_invalid_json"));
                }
            }
        },
        [t, tGlobal, buildFormDataError]
    );

    const handleContentChange = useCallback(
        (newContent: string) => {
            setContent(newContent);
            validateContent(newContent);
        },
        [validateContent]
    );

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(content);
        message.success(t("blueprint.copied"));
    }, [content, t]);

    const handleApply = useCallback(() => {
        let parsed: BlueprintData;
        try {
            parsed = JSON.parse(content) as BlueprintData;
        } catch (err) {
            const location = getJsonErrorLocation(content, err);
            if (location) {
                message.error(
                    t("blueprint.error_invalid_json_at", { line: location.row + 1, column: location.column + 1 })
                );
                setAnnotations([{ ...location, text: (err as Error).message, type: "error" }]);
                editorRef.current?.gotoLine(location.row + 1, location.column, true);
                editorRef.current?.focus();
            } else {
                message.error(t("blueprint.error_invalid_json"));
            }
            return;
        }

        const duplicate = findDuplicateConnection(parsed.edges);
        if (duplicate) {
            message.error(
                t("blueprint.error_duplicate_connection", { source: duplicate.source, target: duplicate.target })
            );
            return;
        }

        const issues = validateBlueprintFormData(parsed);
        if (issues.length > 0) {
            message.error(buildFormDataError(issues));
            return;
        }

        let blueprint = normalizeBlueprintFormData(parsed);
        if (ccEntity) {
            const config = CC_ENTITY_CONFIGS[ccEntity.entityType];
            const { blueprint: adapted, warnings } = adaptBlueprintForEntity(blueprint, ccEntity.entityType, config);
            blueprint = adapted;
            warnings.forEach((w) => message.warning(t(`blueprint.${w.key}`, w.params)));
        }

        onApply(blueprint);
        onClose();
        message.success(t("blueprint.applied"));
    }, [content, ccEntity, onApply, onClose, t, buildFormDataError]);

    // Only recomputed when content changes — used to resolve each listed issue's line for the
    // "show all issues" click-to-jump. Safe to assume valid JSON here: formDataIssues is only
    // ever populated after a successful JSON.parse of this same content.
    const contentAst = useMemo(() => {
        try {
            return parseJsonWithPositions(content);
        } catch {
            return null;
        }
    }, [content]);

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={<DrawerTitle mainTitle={t("blueprint.title")} />}
            width={700}
            extra={
                <Flex gap="small">
                    <Button size="large" icon={<CopyOutlined />} onClick={handleCopy}>
                        {tC("copy")}
                    </Button>
                    <Button size="large" onClick={onClose}>
                        {tC("cancel")}
                    </Button>
                    <Button size="large" type="primary" onClick={handleApply} disabled={!isValid}>
                        {tC("apply")}
                    </Button>
                </Flex>
            }
        >
            <DrawerContent className="p-4">
                <p className="text-gray-500 mb-4">{t("blueprint.description")}</p>
                {error && (
                    <Alert
                        type="error"
                        showIcon
                        message={error}
                        description={
                            formDataIssues.length > 1 ? (
                                <div>
                                    <Button
                                        type="link"
                                        className="!h-auto !p-0"
                                        onClick={() => setShowAllIssues((prev) => !prev)}
                                    >
                                        {showAllIssues
                                            ? t("blueprint.hide_all_issues")
                                            : t("blueprint.show_all_issues")}
                                    </Button>
                                    {showAllIssues && (
                                        <ul className="mt-2 list-disc pl-4">
                                            {formDataIssues.map((issue, index) => {
                                                const row = contentAst ? findIssueLine(contentAst, issue) : null;
                                                return (
                                                    <li key={index}>
                                                        {row === null ? (
                                                            formatBlueprintFormDataIssue(tGlobal, issue)
                                                        ) : (
                                                            <Button
                                                                type="link"
                                                                className="!h-auto !whitespace-normal !p-0 text-left"
                                                                onClick={() => {
                                                                    editorRef.current?.gotoLine(row + 1, 0, true);
                                                                    editorRef.current?.focus();
                                                                }}
                                                            >
                                                                {formatBlueprintFormDataIssue(tGlobal, issue)}
                                                            </Button>
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            ) : undefined
                        }
                        className="mb-4"
                    />
                )}
                <div className={classNames("rounded", { "border border-red-500": !isValid })}>
                    <AceEditor
                        name="blueprint-editor"
                        mode="json"
                        theme="github"
                        value={content}
                        onChange={handleContentChange}
                        onLoad={(editor) => (editorRef.current = editor)}
                        wrapEnabled
                        fontSize={12}
                        width="100%"
                        height={`${editorHeight}px`}
                        className="!w-full"
                    />
                    <div
                        onMouseDown={handleResizeStart}
                        className="flex h-4 cursor-row-resize items-center justify-center rounded-b bg-gray-200 hover:bg-gray-300"
                        title={t("blueprint.resize_editor")}
                    >
                        <HolderOutlined className="rotate-90 text-gray-500" style={{ fontSize: 12 }} />
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
};
